class MegaMenu extends HTMLElement {
  connectedCallback() {
    this.desktopTriggers = this.querySelectorAll('[data-mega-menu-trigger]');
    this.desktopPanels = this.querySelectorAll('[data-mega-menu-panel]');
    this.mobileItems = this.querySelectorAll('[data-mega-menu-mobile-item]');
    this.mobileTriggers = this.querySelectorAll('[data-mega-menu-mobile-trigger]');
    this.mobilePanels = this.querySelectorAll('[data-mega-menu-mobile-panel]');
    this.mobileToggle = this.querySelector('[data-mega-menu-mobile-toggle]');

    this.mobilePanels.forEach((panel) => panel.removeAttribute('hidden'));
    this.desktopPanels.forEach((panel) => panel.removeAttribute('hidden'));

    this.bindDesktop();
    this.bindMobile();
    this.bindTabs();
    this.bindMobileToggle();
    this.bindCarousels();
    this.bindCards();
    this.bindCartDrawer();
    this.bindSearchDrawer();
    this.bindCurrencySelector();
    this.preloadMenuImages();
  }

  // Menu images are loading="lazy" inside collapsed panels, so the browser only
  // starts fetching them when a panel opens — on a slow connection that's a
  // visible multi-second wait. Fetch them once the page has loaded (or on the
  // first touch/hover of the menu, whichever comes first). Only the layout
  // that's actually displayed is fetched, so mobile never downloads the
  // desktop tiles.
  preloadMenuImages() {
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      const visible = this.querySelector(
        window.matchMedia('(min-width: 990px)').matches ? '.mega-menu__desktop' : '.mega-menu__mobile'
      );
      visible?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    };

    ['pointerdown', 'mouseenter', 'focusin'].forEach((type) =>
      this.addEventListener(type, run, { once: true, passive: true })
    );

    const idle = () => (window.requestIdleCallback ? requestIdleCallback(run, { timeout: 3000 }) : setTimeout(run, 1500));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  bindCards() {
    this.querySelectorAll('[data-mega-menu-card-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const card = toggle.closest('[data-mega-menu-card]');
        if (!card) return;
        const willOpen = !card.classList.contains('is-open');

        // Accordion: close any other open card in the same list
        const scope = card.closest('.mega-menu__mcards') || this;
        scope.querySelectorAll('[data-mega-menu-card].is-open').forEach((other) => {
          if (other === card) return;
          other.classList.remove('is-open');
          const otherToggle = other.querySelector('[data-mega-menu-card-toggle]');
          if (otherToggle) otherToggle.setAttribute('aria-expanded', 'false');
        });

        card.classList.toggle('is-open', willOpen);
        toggle.setAttribute('aria-expanded', String(willOpen));
        this.refreshMobileDrawerHeight();
      });
    });
  }

  bindCurrencySelector() {
    this.querySelectorAll('.mega-menu__currency-form select').forEach((select) => {
      select.addEventListener('change', function () {
        this.closest('form').submit();
      });
    });
  }

  bindCartDrawer() {
    this.querySelectorAll('.mega-menu__icon-button--cart').forEach((link) => {
      link.addEventListener('click', (event) => {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;
        event.preventDefault();
        drawer.open(link);
      });
    });
  }

  bindSearchDrawer() {
    this.querySelectorAll('[data-mega-menu-search-trigger]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        const summary = document.querySelector('[id^="Search-drawer-button-"]');
        if (!summary) return;
        event.preventDefault();
        summary.click();
      });
    });
  }

  bindCarousels() {
    this.querySelectorAll('[data-mega-menu-carousel]').forEach((carousel) => {
      const scroller = carousel.querySelector('[data-mega-menu-carousel-scroll]');
      const dots = carousel.querySelector('[data-mega-menu-carousel-dots]');
      if (!scroller || !dots) return;

      const update = () => {
        const pageCount = Math.max(1, Math.ceil(scroller.scrollWidth / scroller.clientWidth));
        if (pageCount <= 1) {
          dots.hidden = true;
          dots.replaceChildren();
          return;
        }
        dots.hidden = false;
        if (dots.children.length !== pageCount) {
          dots.replaceChildren();
          for (let i = 0; i < pageCount; i++) {
            const dot = document.createElement('span');
            dot.className = 'mega-menu__pagination-dot';
            dots.appendChild(dot);
          }
        }
        const active = Math.min(pageCount - 1, Math.round(scroller.scrollLeft / scroller.clientWidth));
        Array.from(dots.children).forEach((dot, i) => {
          dot.classList.toggle('mega-menu__pagination-dot--active', i === active);
        });
      };

      update();
      scroller.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      if ('ResizeObserver' in window) {
        new ResizeObserver(update).observe(scroller);
      }
    });
  }

  bindDesktop() {
    const desktop = this.querySelector('.mega-menu__desktop');
    if (!desktop) return;

    const primaryNav = this.querySelector('.mega-menu__primary-nav');
    const primaryLinks = primaryNav ? primaryNav.querySelectorAll('.mega-menu__primary-link') : [];
    const primaryIndicator = primaryNav ? primaryNav.querySelector('.mega-menu__primary-indicator') : null;
    const activePrimary = primaryNav ? primaryNav.querySelector('.mega-menu__primary-link--active') : null;

    const positionPrimaryIndicator = (link) => {
      if (!primaryIndicator) return;
      if (!link) {
        primaryIndicator.style.opacity = '0';
        return;
      }
      const x = link.offsetLeft;
      const y = link.offsetTop + link.offsetHeight - 2;
      primaryIndicator.style.transform = `translate(${x}px, ${y}px)`;
      primaryIndicator.style.width = link.offsetWidth + 'px';
      primaryIndicator.style.opacity = '1';
    };

    if (activePrimary) positionPrimaryIndicator(activePrimary);

    primaryLinks.forEach((link) => {
      const onEnter = () => {
        positionPrimaryIndicator(link);
        if (!link.hasAttribute('data-mega-menu-trigger')) {
          this.closeDesktopPanels();
        }
      };
      link.addEventListener('mouseenter', onEnter);
      link.addEventListener('focus', onEnter);
    });

    this.desktopTriggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => this.openDesktopPanel(trigger.dataset.megaMenuTrigger));
      trigger.addEventListener('focus', () => this.openDesktopPanel(trigger.dataset.megaMenuTrigger));
    });

    desktop.addEventListener('mouseleave', () => {
      this.closeDesktopPanels();
      positionPrimaryIndicator(activePrimary);
    });
  }

  openDesktopPanel(index) {
    this.desktopTriggers.forEach((t) => {
      t.setAttribute('aria-expanded', t.dataset.megaMenuTrigger === index ? 'true' : 'false');
    });
    this.desktopPanels.forEach((panel) => {
      panel.classList.toggle('mega-menu__panel--open', panel.dataset.megaMenuPanel === index);
    });
  }

  closeDesktopPanels() {
    this.desktopTriggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.desktopPanels.forEach((panel) => panel.classList.remove('mega-menu__panel--open'));
  }

  bindMobile() {
    this.mobileTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const index = trigger.dataset.megaMenuMobileTrigger;
        const item = this.querySelector(`[data-mega-menu-mobile-item="${index}"]`);
        const isExpanded = item && item.classList.contains('mega-menu__mobile-item--expanded');
        if (isExpanded) {
          this.collapseMobileItem();
        } else {
          this.expandMobileItem(index);
        }
        this.refreshMobileDrawerHeight();
      });
    });
  }

  refreshMobileDrawerHeight() {
    if (this.mobileContainer && this.mobileContainer.classList.contains('mega-menu__mobile--open') && this.mobileDrawer) {
      this.mobileDrawer.style.maxHeight = 'none';
    }
  }

  expandMobileItem(index) {
    this.mobileItems.forEach((item) => {
      item.classList.toggle('mega-menu__mobile-item--expanded', item.dataset.megaMenuMobileItem === index);
    });
    this.mobilePanels.forEach((panel) => {
      panel.classList.toggle('mega-menu__mobile-panel--open', panel.dataset.megaMenuMobilePanel === index);
    });
  }

  collapseMobileItem() {
    this.mobileItems.forEach((item) => item.classList.remove('mega-menu__mobile-item--expanded'));
    this.mobilePanels.forEach((panel) => panel.classList.remove('mega-menu__mobile-panel--open'));
  }

  bindMobileToggle() {
    if (!this.mobileToggle) return;
    this.mobileContainer = this.querySelector('.mega-menu__mobile');
    this.mobileDrawer = this.querySelector('.mega-menu__mobile-drawer');
    this.mobileToggle.setAttribute('aria-expanded', 'false');
    this.mobileToggle.addEventListener('click', () => {
      const isOpen = this.mobileToggle.getAttribute('aria-expanded') === 'true';
      const hasExpanded = this.querySelector('.mega-menu__mobile-item--expanded');
      if (isOpen && hasExpanded) {
        this.collapseMobileItem();
        return;
      }
      const next = !isOpen;
      this.mobileToggle.setAttribute('aria-expanded', String(next));
      if (this.mobileContainer) {
        this.mobileContainer.classList.toggle('mega-menu__mobile--open', next);
      }
      this.animateMobileDrawer(next);
      if (!next) this.collapseMobileItem();
    });
  }

  animateMobileDrawer(open) {
    const drawer = this.mobileDrawer;
    if (!drawer) return;
    drawer.style.maxHeight = drawer.scrollHeight + 'px';
    if (open) {
      const onEnd = (e) => {
        if (e.propertyName !== 'max-height') return;
        drawer.removeEventListener('transitionend', onEnd);
        if (this.mobileContainer && this.mobileContainer.classList.contains('mega-menu__mobile--open')) {
          drawer.style.maxHeight = 'none';
        }
      };
      drawer.addEventListener('transitionend', onEnd);
    } else {
      requestAnimationFrame(() => {
        drawer.style.maxHeight = '0px';
      });
    }
  }

  bindTabs() {
    this.querySelectorAll('[data-mega-menu-panel]').forEach((panel) => {
      const tabs = panel.querySelectorAll('[data-mega-menu-tab]');
      const tabPanels = panel.querySelectorAll('[data-mega-menu-tab-panel]');
      const nav = panel.querySelector('.mega-menu__tabs:not(.mega-menu__tabs--scroll)');
      const indicator = nav ? nav.querySelector('.mega-menu__tabs-indicator') : null;

      const setActive = (activeIdx) => {
        tabPanels.forEach((p) => {
          const idx = parseInt(p.dataset.megaMenuTabPanel, 10);
          p.classList.toggle('mega-menu__tiles--active', idx === activeIdx);
          p.classList.toggle('mega-menu__tiles--past', idx < activeIdx);
        });
      };

      const positionIndicator = (tab) => {
        if (!indicator) return;
        const x = tab.offsetLeft;
        const y = tab.offsetTop + tab.offsetHeight - 2;
        indicator.style.transform = `translate(${x}px, ${y}px)`;
        indicator.style.width = tab.offsetWidth + 'px';
      };

      tabPanels.forEach((p) => p.removeAttribute('hidden'));
      setActive(0);

      const firstActive = panel.querySelector('.mega-menu__tab--active');
      if (firstActive) positionIndicator(firstActive);

      const cta = panel.querySelector('[data-mega-menu-cta]');
      const updateCta = (tab) => {
        if (!cta) return;
        const url = tab.dataset.megaMenuTabUrl;
        const title = tab.dataset.megaMenuTabTitle || tab.textContent.trim();
        if (url && url !== '#') {
          cta.setAttribute('href', url);
          cta.hidden = false;
        } else {
          cta.hidden = true;
        }
        cta.textContent = `Shop ${title}`;
      };

      const activate = (tab) => {
        const idx = parseInt(tab.dataset.megaMenuTab, 10);
        tabs.forEach((t) => t.classList.toggle('mega-menu__tab--active', t === tab));
        setActive(idx);
        positionIndicator(tab);
        updateCta(tab);
      };

      tabs.forEach((tab) => {
        tab.addEventListener('mouseenter', () => activate(tab));
        tab.addEventListener('focus', () => activate(tab));
        tab.addEventListener('click', () => activate(tab));
      });
    });

    this.querySelectorAll('[data-mega-menu-mobile-panel]').forEach((panel) => {
      const tabs = panel.querySelectorAll('[data-mega-menu-mobile-tab]');
      const tabPanels = panel.querySelectorAll('[data-mega-menu-mobile-tab-panel]');

      const setActive = (activeIdx) => {
        tabPanels.forEach((p) => {
          const idx = parseInt(p.dataset.megaMenuMobileTabPanel, 10);
          p.classList.toggle('mega-menu__list--active', idx === activeIdx);
          p.classList.toggle('mega-menu__list--past', idx < activeIdx);
        });
      };

      tabPanels.forEach((p) => p.removeAttribute('hidden'));
      setActive(0);

      const cta = panel.querySelector('[data-mega-menu-cta]');
      const updateCta = (tab) => {
        if (!cta) return;
        const url = tab.dataset.megaMenuTabUrl;
        const title = tab.dataset.megaMenuTabTitle || tab.textContent.trim();
        if (url && url !== '#') {
          cta.setAttribute('href', url);
          cta.hidden = false;
        } else {
          cta.hidden = true;
        }
        cta.textContent = `Shop ${title}`;
      };

      const animateScrollLeft = (el, target, duration = 650) => {
        const start = el.scrollLeft;
        const max = el.scrollWidth - el.clientWidth;
        const end = Math.max(0, Math.min(max, target));
        const distance = end - start;
        if (Math.abs(distance) < 1) return;
        const startTime = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        if (el._mmScrollRaf) cancelAnimationFrame(el._mmScrollRaf);
        const prevSnap = el.style.scrollSnapType;
        el.style.scrollSnapType = 'none';
        const step = (now) => {
          const t = Math.min(1, (now - startTime) / duration);
          el.scrollLeft = start + distance * ease(t);
          if (t < 1) {
            el._mmScrollRaf = requestAnimationFrame(step);
          } else {
            el.style.scrollSnapType = prevSnap;
            el._mmScrollRaf = null;
          }
        };
        el._mmScrollRaf = requestAnimationFrame(step);
      };

      const centerTab = (tab) => {
        const scroller = tab.closest('[data-mega-menu-carousel-scroll]');
        if (!scroller) return;
        const scrollerRect = scroller.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        const tabLeft = tabRect.left - scrollerRect.left + scroller.scrollLeft;
        const target = tabLeft - (scroller.clientWidth / 2) + (tab.offsetWidth / 2);
        animateScrollLeft(scroller, target, 650);
      };

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const idx = parseInt(tab.dataset.megaMenuMobileTab, 10);
          tabs.forEach((t) => t.classList.toggle('mega-menu__tab--active', t === tab));
          setActive(idx);
          centerTab(tab);
          updateCta(tab);
        });
      });
    });
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}

